import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { LoaderCircle, Search, Trash2, UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import * as Query from '../../../api/gql/query'
import ConfirmDialog from '../../schedule/components/ConfirmDialog'
import type { CostContractRepresentative } from '../types'

interface Member { id: string; name: string; email: string | null }
interface Props {
  projectId: string
  representatives: CostContractRepresentative[]
  canEdit: boolean
  saving: boolean
  /** Resolve true when the server accepted the change. */
  onAdd: (userId: string) => Promise<boolean>
  onRemove: (id: string) => Promise<boolean>
}

/** "Zástupci zhotovitele" (1b-B spec §8): project members who see this contract and submit its payment applications. */
const ContractRepresentativesCard: React.FC<Props> = ({ projectId, representatives, canEdit, saving, onAdd, onRemove }) => {
  const { t } = useTranslation()
  const [adding, setAdding] = useState(false)
  const [search, setSearch] = useState('')
  const [pickedId, setPickedId] = useState<string | null>(null)
  const [removing, setRemoving] = useState<CostContractRepresentative | null>(null)
  const { data, loading } = useQuery(Query.PROJECT_MEMBERS, {
    variables: { projectId },
    skip: !adding,
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const taken = useMemo(() => new Set(representatives.map((r) => r.userId)), [representatives])
  const q = search.trim().toLowerCase()
  const members: Member[] = (data?.project?.users ?? [])
    .map((m: { user: Member | null }) => m.user)
    .filter((u: Member | null): u is Member => !!u && !taken.has(u.id))
    .filter((u: Member) => !q || [u.name, u.email].some((s) => !!s && s.toLowerCase().includes(q)))
  const date = (iso: string) => new Date(iso).toLocaleDateString(t('date_locale') || 'cs-CZ')

  const add = async (userId: string) => {
    setPickedId(userId)
    const ok = await onAdd(userId)
    setPickedId(null)
    if (ok) {
      setAdding(false)
      setSearch('')
    }
  }
  const remove = async () => {
    if (!removing) return
    if (await onRemove(removing.id)) setRemoving(null)
  }

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">{t('CostControl_representatives_title')}</CardTitle>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
            <UserPlus className="h-4 w-4" />
            {t('CostControl_representatives_add')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">{t('CostControl_representatives_hint')}</p>
        {representatives.length === 0 && <p className="text-sm text-muted-foreground">{t('CostControl_representatives_empty')}</p>}
        {representatives.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-2 rounded-md border px-3 py-2">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{r.name}</div>
              <div className="truncate text-xs text-muted-foreground">
                {[r.email, t('CostControl_representatives_added', { date: date(r.createdAt), name: r.createdByName ?? '—' })].filter(Boolean).join(' · ')}
              </div>
            </div>
            {canEdit && (
              <Button variant="ghost" size="sm" aria-label={t('CostControl_representatives_remove')} onClick={() => setRemoving(r)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </CardContent>
      <Dialog open={adding} onOpenChange={(open) => { if (!open && !pickedId) { setAdding(false); setSearch('') } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('CostControl_representatives_add_title')}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">{t('CostControl_representatives_hint')}</p>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder={t('Common_search')} value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-8 text-sm" />
          </div>
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {loading && !data && <div className="flex justify-center p-4"><LoaderCircle className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
            {!loading && members.length === 0 && <p className="p-2 text-sm text-muted-foreground">{t('CostControl_representatives_no_members')}</p>}
            {members.map((u) => (
              <Button key={u.id} variant="ghost" className="h-auto w-full justify-between px-3 py-2 text-left" disabled={saving} onClick={() => { void add(u.id) }}>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{u.name}</span>
                  {u.email && <span className="block truncate text-xs text-muted-foreground">{u.email}</span>}
                </span>
                {pickedId === u.id ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4 text-muted-foreground" />}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!removing}
        title={t('CostControl_representatives_remove_title')}
        description={removing ? t('CostControl_representatives_remove_text', { name: removing.name }) : undefined}
        confirmLabel={t('CostControl_representatives_remove')}
        variant="destructive"
        submitting={saving}
        onCancel={() => setRemoving(null)}
        onConfirm={() => { void remove() }}
      />
    </Card>
  )
}
export default ContractRepresentativesCard
